import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  Typography,
  Select,
  Divider,
  Space,
  Tooltip
} from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { Line } from "@ant-design/plots";
import moment from "moment";
import FileConstants from "../../../../FileConstants";
import DateRangePicker from "../../DateRangePicker/DateRangePicker";
import ENVIRONMENT from '../../../../environments';
import axios from 'axios';
import PresetRangeSelector from "./PresetRangeSelector";
import ViewTypeSelector from "./ViewTypeSelector";
import { processChartData, getPresetDateRange, addWeekendAnnotations } from "./chartUtils";

const { Title } = Typography;
const { Option } = Select;

const DATE_FORMAT = "YYYY/MM/DD";
const DEFAULT_DATE_RANGES = {
  start: moment(moment().startOf('month').format("YYYY/MM/DD")),
  end: moment(moment().format("YYYY/MM/DD"))
};

const QAChart = ({ 
  chartStyle, 
  companyList,
  setIsLoading
}) => {
  const [qaFilter, setQaFilter] = useState(undefined);
  const [companyFilter, setCompanyFilter] = useState(undefined);
  const [qaViewType, setQaViewType] = useState("Daily");
  const [qaDataStartRange, setQaDataStartRange] = useState(DEFAULT_DATE_RANGES.start);
  const [qaDataEndRange, setQaDataEndRange] = useState(DEFAULT_DATE_RANGES.end);
  const [qaChartData, setQaChartData] = useState([]);
  const [qaPresetRange, setQaPresetRange] = useState(undefined);
  const [adminList, setAdminList] = useState([]);

  const onQaCalenderChange = (date_values) => {
    if (date_values && date_values[0] && date_values[1]) {
      setQaDataStartRange(date_values[0]);
      setQaDataEndRange(date_values[1]);
      setQaPresetRange(undefined);
    }
  };

  const fetchQaChartData = useCallback(async () => {
    try {
      setIsLoading(true);
      let startDate, endDate;
      
      // Always use local date range
      startDate = moment(qaDataStartRange).format("YYYY-MM-DD");
      endDate = moment(qaDataEndRange).format("YYYY-MM-DD");
      
      const payload = {
        action: "get_admin_qa_stats",
        start_date: startDate,
        end_date: endDate
      };

      // Add qa_person to payload if a QA person is selected
      if (qaFilter) {
        payload.qa_person = qaFilter;
      }

      // Add company_id to payload if a company is selected
      if (companyFilter && companyFilter !== "ALL") {
        payload.company_id = companyFilter;
      }

      const response = await axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, payload);
      console.log("QA chart data response:", response.data);
      setQaChartData(response.data || []);
    } catch (error) {
      console.error("Error fetching QA chart data:", error);
      setQaChartData([]);
    } finally {
      setIsLoading(false);
    }
  }, [qaDataStartRange, qaDataEndRange, qaFilter, companyFilter, setIsLoading]);

  useEffect(() => {
    fetchQaChartData();
  }, [fetchQaChartData]);


  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        setIsLoading(true);
        const response = await axios.post(ENVIRONMENT.GET_USER_GROUPS,  {
            group_names: ["Admin",'SuperAdmin']
        });
        console.log("Admin list response:", response.data);
        if (response.data?.length) {
            response.data.sort((a, b) => a.localeCompare(b));
        }
        setAdminList(response.data || []);
      } catch (error) {
        console.error("Error fetching admin list:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchAdmins();

  }, []);

  const filteredQaData = useMemo(() => {
    return processChartData(
      qaChartData,
      qaViewType,
      DATE_FORMAT,
      qaDataStartRange ? moment(qaDataStartRange).format("YYYY-MM-DD") : null,
      qaDataEndRange ? moment(qaDataEndRange).format("YYYY-MM-DD") : null
    );
  }, [qaChartData, qaViewType, qaDataStartRange, qaDataEndRange]);

  const qaChartConfig = useMemo(() => {
    const baseConfig = FileConstants.LINE_PLOT_CONFIG(
      filteredQaData,
      "date2",
      "value",
      "type"
    );
    
    // Add weekend annotations only for daily view
    return addWeekendAnnotations(baseConfig, filteredQaData, "date2", qaViewType);
  }, [filteredQaData, qaViewType]);

  // Handle QA preset range change
  const handleQaPresetChange = (value) => {
    setQaPresetRange(value);
    if (value) {
      const dateRange = getPresetDateRange(value);
      if (dateRange) {
        setQaDataStartRange(dateRange.start);
        setQaDataEndRange(dateRange.end);
      }
    }else{
        setQaDataStartRange(DEFAULT_DATE_RANGES.start);
        setQaDataEndRange(DEFAULT_DATE_RANGES.end);
    }
  };

  return (
    <Col span={24}>
      <Card style={chartStyle}>
        <Row justify="space-between" align="middle">
          <Title level={4}>Admin QA Throughput{' '}
              <Tooltip title="Volume of models reviewed regardless of whether the model was already rejected by Admin QA or customer">
                <QuestionCircleOutlined style={{ color: '#8c8c8c' }} />
              </Tooltip>

          </Title>
          <Row gutter={12}>
            <Col>
              <Select
                className='admin-charts-filter'
                onChange={setQaFilter}
                style={{ width: 180 }}
                placeholder="Select QA Admin"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.props.children.toLowerCase().includes(input.trim().toLowerCase())
                }
              >
                { adminList.map((admin) => (
                  <Option key={admin} value={admin}>
                    {admin}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col>
              <Select
                className='admin-charts-filter'
                value={companyFilter}
                onChange={setCompanyFilter}
                style={{ width: 180 }}
                placeholder="Select Company"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.props.children.toLowerCase().includes(input.trim().toLowerCase())
                }
              >
                <Option value="ALL">ALL</Option>
                {companyList.map(company => (
                    <Select.Option key={company.companyID} value={company.companyID}>
                    {company.companyName}
                    </Select.Option>
                ))}
              </Select>
            </Col>
            
            <Col>
              <ViewTypeSelector
                value={qaViewType}
                onChange={setQaViewType}
              />
            </Col>
            
            <Col>
              <Space>
                <DateRangePicker
                  start={qaDataStartRange}
                  end={qaDataEndRange}
                  date_format={DATE_FORMAT}
                  onCalChange={onQaCalenderChange}
                />
              </Space>
            </Col>
            
            <Col>
              <PresetRangeSelector
                value={qaPresetRange}
                onChange={handleQaPresetChange}
              />
            </Col>
          </Row>
        </Row>

        <Divider style={{ margin: "16px 0" }} />
        <Line {...qaChartConfig} />
      </Card>
    </Col>
  );
};

export default QAChart;
